import React from 'react';
import { useToast } from '@/hooks/use-toast';
import { t } from '@/i18n/strings';
import { useI18n } from '@/i18n/useI18n';
import { authDebug } from '@/lib/authDebugInstrumentation';
import { FaArrowRight } from "react-icons/fa";


interface State { hasError: boolean; message: string | null; }

export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, message: null };
  static getDerivedStateFromError(error: unknown) {
    authDebug('GlobalErrorBoundary.getDerivedStateFromError', {});
    return {
      hasError: true,
      message: error instanceof Error ? error.message : 'Unexpected error',
    };
  }
  componentDidCatch(error: any, info: any) {
    authDebug('GlobalErrorBoundary.componentDidCatch', {
      message: error?.message ?? String(error),
      componentStackPresent: Boolean(info?.componentStack),
    });
    console.error('Unexpected error boundary catch', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        // Contenedor principal: Sin tarjetas, el fondo blanco completo actúa como el lienzo (Invisible UI).
    <div className="min-h-screen bg-white dark:bg-zinc-950 flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-24 font-sans transition-colors duration-300">
      
      {/* Diseño a dos columnas en pantallas grandes para balancear la imagen y el texto */}
      <div className="max-w-7xl mx-auto w-full flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-24">
        
        {/* Bloque de Texto y Llamado a la Acción */}
        <div className="flex-1 w-full text-center lg:text-left flex flex-col items-center lg:items-start">
          {/* Tipografía grande, de alto contraste y sin adornos */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-zinc-900 dark:text-white tracking-tight leading-tight mb-6">
            Hemos perdido el rastro de esta propiedad.
          </h1>
          
          {/* Mensaje cálido y enfocado en el modelo de arrendamiento */}
          <p className="text-lg sm:text-xl text-zinc-500 dark:text-zinc-400 font-light mb-10 max-w-lg leading-relaxed">
            Parece que hubo un inconveniente técnico o la propiedad que buscas ya no está disponible para arrendamiento. No te preocupes, te ayudamos a volver al inicio para que continúes gestionando o buscando tu próximo hogar.
          </p>
          
          {/* Botón de acción: Verde Esmeralda, redondeado completo (rounded-full) y con borde */}
          <button onClick={() => window.location.href = '/'} className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-emerald-600 text-white text-lg font-medium rounded-full border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 dark:focus:ring-offset-zinc-950 shadow-sm">
            Volver al inicio
            <FaArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Bloque de Imagen Premium: Libre, sin bordes ni fondos para que respire */}
        <div className="flex-1 w-full max-w-2xl">
          <div className="relative w-full">
            <img
              src="/generic_actions/not_found_image.webp"
              alt="Propiedad no encontrada"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

      </div>
    </div>
      );
    }

    return this.props.children;
  }
}

export function GlobalErrorBoundary({ children }: { children: React.ReactNode }) {
  const { lang } = useI18n();
  const { toast } = useToast();
  return (
    <AppErrorBoundary>
      <ErrorNotifier toastFn={toast} lang={lang} />
      {children}
    </AppErrorBoundary>
  );
}

// Dumb component to subscribe to window unhandled errors and show toast
function ErrorNotifier({ toastFn, lang }: { toastFn: any; lang: any }) {
  React.useEffect(() => {
    let lastToastAt = 0;
    const ignoreMsgPatterns = [
      /ResizeObserver loop (limit exceeded|completed)/i,
      /Unable to preventDefault inside passive event listener/i,
      /NetworkError( when attempting to fetch resource)?/i,
      /The operation was aborted/i,
      /Script error/i,
      /Failed to load resource/i,
      /The (resource|source) (image )?could not be decoded/i,
      /Decoding failed.*/i,
    ];
    const shouldIgnoreMessage = (msg: string) => ignoreMsgPatterns.some((r) => r.test(msg));
    const maybeToast = (message: string) => {
      if (!message || shouldIgnoreMessage(message)) return;
      const now = Date.now();
      if (now - lastToastAt < 8000) return; // rate limit
      lastToastAt = now;
      toastFn({ title: t('errors.unexpected', lang), description: message, variant: 'destructive' });
    };

    // Only surface actual runtime errors, ignore resource load errors (e.g., image/script/link failures)
    const onError = (event: Event) => {
      // ErrorEvent has message/error; resource load Event does not
      if (event instanceof ErrorEvent) {
        const message = event.message || (event.error && String(event.error)) || 'Unexpected error';
        maybeToast(message);
        return;
      }
      // Ignore non-Error events to prevent noisy toasts on resource failures (e.g., AVIF not supported)
    };

    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      // Some browsers surface benign DOMExceptions (e.g., AbortError). Filter those out.
  const reason: any = e.reason;
      const name = reason?.name || '';
      if (name === 'AbortError') return;
  const msg = (reason && (reason.message || reason.toString?.())) || String(reason);
  maybeToast(msg);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [toastFn, lang]);
  return null;
}
